import React, { useRef, useContext, useEffect } from 'react';
import { ChatbotContext } from './src-contexts-ChatbotContext.js';
import { CAContext } from './src-contexts-CAContext.js';
import { useCBChatStart } from './src-hooks-chatbot-useCBChatStart.js';
import { useCBChatMessage } from './src-hooks-chatbot-useCBChatMessage.js';
import { useCBChatEnd } from './src-hooks-chatbot-useCBChatEnd.js';
import { useCBChatRetrieve } from './src-hooks-chatbot-useCBChatRetrieve.js';
import TypingIndicator from './src-components-TypingIndicator-TypingIndicator.js';
import { util } from './src-utils-miaw-common.js';
import { setChatSessionType } from './src-utils-session-storage-utils.js';
import { useCustomTracking } from './src-hooks-custom-hooks-useTracking.js';
import { useNRLogging } from './src-hooks-custom-hooks-useNRLogging.js';
import { useCustomChatEvents } from './src-hooks-custom-hooks-useCustomChatEvents.js';

const useChatbotHooks = () => {
  const { sendTrackingEvent } = useCustomTracking();
  const prevUnreadMessagesRef = useRef(0);
  const { logCustomAttributesNR } = useNRLogging();
  const { sendChatEvents } = useCustomChatEvents();
  const {
    chatState: { chatWindow, chatSession, unreadMessages, playSoundMessageNotif },
    setChatState
  } = useContext(CAContext);
  const {
    state: { customFlow },
    setState: setChatbotContext
  } = useContext(ChatbotContext);
  const { data: chatbotStartData, trigger: triggerStartChatBot, loading: chatbotStartLoading, error: chatbotStartError } = useCBChatStart();
  const {
    data: chatbotMessageData,
    trigger: triggerChatbotMessage,
    cancel: cancelChatbotMessage,
    loading: chatbotMessageLoading,
    error: chatbotMessageError
  } = useCBChatMessage();
  const { data: chatbotEndData, trigger: triggerChatbotEnd, loading: chatbotEndLoading, error: chatbotEndError } = useCBChatEnd();
  const { data: chatbotRetrieveData, trigger: triggerChatbotRetrieve, loading: chatbotRetrieveLoading, error: chatbotRetrieveError } = useCBChatRetrieve();
  useEffect(() => {
    if (chatbotStartData) {
      setChatbotContext((prevState) => ({
        ...prevState,
        conversationMessages: [
          ...prevState.conversationMessages
          // chatbotStartData.message, TODO : message will be hardcoded for now until API is fast enough to return a message
        ],
        sessionId: chatbotStartData.sessionId,
        discussionId: chatbotStartData.discussionId
      }));
      setChatSessionType("chatbotSession");
    }
  }, [chatbotStartData]);
  useEffect(() => {
    if (chatbotMessageData) {
      setChatbotContext((prevState) => ({
        ...prevState,
        conversationMessages: [...prevState.conversationMessages, ...chatbotMessageData.messages]
      }));
    }
  }, [chatbotMessageData]);
  useEffect(() => {
    if (chatbotMessageData) {
      const messageCount = chatbotMessageData.messages.length;
      setChatState((prevState) => ({
        ...prevState,
        unreadMessages: prevState.unreadMessages ? prevState.unreadMessages + messageCount : messageCount
      }));
    }
  }, [chatbotMessageData]);
  useEffect(() => {
    const currentUnreadCount = unreadMessages || 0;
    const shouldPlayAudio = currentUnreadCount > prevUnreadMessagesRef.current && currentUnreadCount > 0 && (document.hidden || chatWindow === "minimized");
    if (shouldPlayAudio) {
      playSoundMessageNotif?.();
    }
    prevUnreadMessagesRef.current = currentUnreadCount;
  }, [unreadMessages, chatWindow, playSoundMessageNotif]);
  useEffect(() => {
    if (chatbotMessageData) {
      const flowType = chatbotMessageData.flowType;
      if (flowType) {
        if (flowType.agentConnect) {
          return;
        }
        if (!customFlow || customFlow.flowType !== flowType?.flowType) {
          setChatbotContext((prevState) => ({
            ...prevState,
            customFlow: flowType
          }));
          sendTrackingEvent("Chatbot Clicked", "Chatbot:OrderFlow", "chatbotFlowStarted", "GEN AI Chatbot", {
            chatbotFlowId: flowType?.flowType || "",
            chatbotFlowStep: flowType?.flowStep || "",
            chatbotFlowAdditionalDetails: flowType?.additionalInfo || ""
          });
          logCustomAttributesNR("ca-chatbot-info", {
            logName: "chatbot_flow_started",
            logLevel: "info",
            message: `User started a new chatbot flow : ${flowType?.flowType || ""}`,
            flowType: flowType?.flowType || "",
            flowStep: flowType?.flowStep || ""
          });
        } else if (chatbotMessageData.flowType?.flowStep === "tracking_response" || // For order tracking flow
        chatbotMessageData.flowType?.flowStep === "products_cancelled" || // For order cancellation flow
        chatbotMessageData.flowType?.flowStep === "escalation") {
          setChatbotContext((prevState) => ({
            ...prevState,
            customFlow: void 0
          }));
          sendTrackingEvent("Chatbot Clicked", "Chatbot:OrderFlow", "chatbotFlowCompleted", "GEN AI Chatbot", {
            chatbotFlowId: flowType?.flowType || "",
            chatbotFlowStep: flowType?.flowStep || "",
            chatbotFlowAdditionalDetails: flowType?.additionalInfo || ""
          });
          logCustomAttributesNR("ca-chatbot-info", {
            logName: "chatbot_flow_completed",
            logLevel: "info",
            message: `User completed a chatbot flow : ${flowType?.flowType || ""}`,
            flowType: flowType?.flowType || "",
            flowStep: flowType?.flowStep || ""
          });
        } else {
          sendTrackingEvent("Chatbot Clicked", "Chatbot:OrderFlow", "chatbotFlowStep", "GEN AI Chatbot", {
            chatbotFlowId: flowType?.flowType || "",
            chatbotFlowStep: flowType?.flowStep || "",
            chatbotFlowAdditionalDetails: flowType?.additionalInfo || ""
          });
          logCustomAttributesNR("ca-chatbot-info", {
            logName: "chatbot_flow_step",
            logLevel: "info",
            message: `User progressed to chatbot flow step : ${flowType?.flowStep || ""}`,
            flowType: flowType?.flowType || "",
            flowStep: flowType?.flowStep || ""
          });
        }
      } else {
        setChatbotContext((prevState) => ({
          ...prevState,
          customFlow: void 0
        }));
      }
    }
  }, [chatbotMessageData]);
  useEffect(() => {
    if (chatbotMessageLoading) {
      setChatbotContext((prevState) => ({
        ...prevState,
        conversationMessages: [
          ...prevState.conversationMessages,
          {
            type: "Typing",
            id: util.generateUUID(),
            messageContent: /* @__PURE__ */ React.createElement(TypingIndicator, { senderType: "chatbot", key: util.generateUUID() }),
            timestamp: (/* @__PURE__ */ new Date()).getTime(),
            senderDisplayName: "Chatbot",
            showFeedback: false
          }
        ]
      }));
    } else {
      setChatbotContext((prevState) => ({
        ...prevState,
        conversationMessages: prevState.conversationMessages.filter((item) => item.type !== "Typing")
      }));
    }
  }, [chatbotMessageLoading]);
  useEffect(() => {
    if (chatWindow === "minimized") {
      setChatbotContext((prevState) => ({
        ...prevState,
        conversationMessages: prevState.conversationMessages.map((message) => ({ ...message, isMessageRead: true }))
      }));
    }
  }, [chatWindow]);
  useEffect(() => {
    if (chatbotEndData) {
      setChatState((prevState) => ({
        ...prevState,
        chatSession: "endChatSession"
      }));
    }
  }, [chatbotEndData]);
  useEffect(() => {
    if (chatSession === "initial")
      setChatbotContext((prevState) => ({
        ...prevState,
        endChatData: void 0,
        endChatLoading: false
      }));
  }, [chatSession]);
  useEffect(() => {
    setChatbotContext((prevState) => ({
      ...prevState,
      endChatLoading: chatbotEndLoading
    }));
  }, [chatbotEndLoading]);
  useEffect(() => {
    if (chatbotRetrieveData && (chatSession === "chatbotSession" || chatSession === "miawChatSession")) {
      setChatbotContext((prevState) => ({
        ...prevState,
        conversationMessages: [...chatbotRetrieveData.messages, ...prevState.conversationMessages],
        sessionId: chatbotRetrieveData.sessionId,
        discussionId: chatbotRetrieveData.discussionId
      }));
      sendChatEvents("onSessionContinuation", {});
    }
  }, [chatbotRetrieveData]);
  useEffect(() => {
    setChatbotContext((prevState) => ({
      ...prevState,
      startChatbotSession: triggerStartChatBot
    }));
  }, [triggerStartChatBot]);
  useEffect(() => {
    setChatbotContext((prevState) => ({
      ...prevState,
      sendChatbotMessage: triggerChatbotMessage
    }));
  }, [triggerChatbotMessage]);
  useEffect(() => {
    setChatbotContext((prevState) => ({
      ...prevState,
      endChatbotSession: triggerChatbotEnd
    }));
  }, [triggerChatbotEnd]);
  useEffect(() => {
    setChatbotContext((prevState) => ({
      ...prevState,
      isSendMessageLoading: chatbotMessageLoading
    }));
  }, [chatbotMessageLoading]);
  useEffect(() => {
    setChatbotContext((prevState) => ({
      ...prevState,
      isSendMessageLoading: chatbotStartLoading
    }));
  }, [chatbotStartLoading]);
  useEffect(() => {
    setChatbotContext((prevState) => ({
      ...prevState,
      isEndChatLoading: chatbotRetrieveLoading
    }));
  }, [chatbotRetrieveLoading]);
  useEffect(() => {
    setChatbotContext((prevState) => ({
      ...prevState,
      stopChatbotMessage: cancelChatbotMessage
    }));
  }, [cancelChatbotMessage]);
  useEffect(() => {
    setChatbotContext((prevState) => ({
      ...prevState,
      retrieveChatbotSession: triggerChatbotRetrieve
    }));
  }, [triggerChatbotRetrieve]);
  useEffect(() => {
    const hasErrors = chatbotStartError || chatbotMessageError;
    if (hasErrors) {
      setChatbotContext((prevState) => ({
        ...prevState,
        showErrorMessage: true
      }));
      console.error("Chatbot Error:", {
        startError: chatbotStartError,
        messageError: chatbotMessageError
      });
    } else {
      setChatbotContext((prevState) => ({
        ...prevState,
        showErrorMessage: false
      }));
    }
  }, [chatbotStartError, chatbotMessageError]);
  useEffect(() => {
    if (chatSession === "initial") {
      setChatbotContext((prevState) => ({
        ...prevState,
        showErrorMessage: false
      }));
    }
  }, [chatSession]);
  useEffect(() => {
    if (chatbotEndError) {
      setChatbotContext((prevState) => ({
        ...prevState,
        isChatbotEndError: true
      }));
    }
  }, [chatbotEndError]);
  useEffect(() => {
    if (chatSession === "initial") {
      setChatbotContext((prevState) => ({
        ...prevState,
        isChatbotEndError: false
      }));
    }
  }, [chatSession]);
  return {
    chatbotHooksData: {
      chatbotStartData,
      chatbotMessageData,
      chatbotEndData,
      chatbotRetrieveData
    },
    chatbotHooksLoading: {
      chatbotStartLoading,
      chatbotMessageLoading,
      chatbotEndLoading,
      chatbotRetrieveLoading
    },
    chatbotHooksError: {
      chatbotStartError,
      chatbotMessageError,
      chatbotEndError,
      chatbotRetrieveError
    },
    chatbotHooksTriggers: {
      triggerStartChatBot,
      triggerChatbotMessage,
      triggerChatbotEnd,
      cancelChatbotMessage,
      triggerChatbotRetrieve
    }
  };
};

export { useChatbotHooks };
//# sourceMappingURL=src-hooks-useChatbotHook.js.map
